// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import type {
  ExtractedTransactions,
  TransactionEntry,
  TransactionEntryType,
  TransactionLeg,
  TransactionLegAction,
} from '../types/extraction';
import type { LoadedPdf, PdfTextItem } from './pdfTextLoader';
import { groupItemsByRow, fullText } from './pdfTextLoader';

/**
 * Parses the Standard Life Synergy PRSA *transactions* statement into a
 * structured `ExtractedTransactions`. The transactions table is a 4-column
 * grid (Date | Details | Payments in | Payments out) repeating across
 * pages 4 → ~19; sub-rows belong to the most recent header row.
 *
 * Strategy: walk the row-grouped text items per page, classify each row
 * by its left-most token, and attach legs to the parent header row.
 */
export function extractTransactionsStatement(pdf: LoadedPdf): ExtractedTransactions {
  const all = fullText(pdf);
  const out: ExtractedTransactions = { entries: [] };

  // ---------- Period / summary ----------
  const period = all.match(
    /Statement\s+(?:from|between)\s+(\d{1,2}\s+[A-Za-z]+\s+\d{4})\s*(?:to|-|–|and)\s*(\d{1,2}\s+[A-Za-z]+\s+\d{4})/i,
  );
  if (period) {
    out.periodFrom = parseLongDate(period[1]);
    out.periodTo = parseLongDate(period[2]);
  }

  // Require the currency symbol so we don't accidentally capture a day
  // number from the surrounding date text ("20 June 2024").
  const opening = all.match(/Start date[\s\S]{0,80}?[€¤]\s*([\d.,]+)/i);
  if (opening) out.openingValue = parseAmount(opening[1]);

  const closing = all.match(/End date[\s\S]{0,80}?[€¤]\s*([\d.,]+)/i);
  if (closing) out.closingValue = parseAmount(closing[1]);

  // The authoritative totals come from the final "Totals" row of the
  // transactions table (e.g. `Totals ¤17,648.00 ¤0.00`).
  const totalsRow = all.match(/Totals?\s*[€¤]\s*([\d.,]+)\s*[€¤]\s*([\d.,]+)/i);
  if (totalsRow) {
    out.totalPaymentsIn = parseAmount(totalsRow[1]);
    out.totalPaymentsOut = parseAmount(totalsRow[2]);
  } else {
    const paymentsIn = all.match(/Payments? from[\s\S]{0,80}?[€¤]\s*([\d.,]+)/i);
    if (paymentsIn) out.totalPaymentsIn = parseAmount(paymentsIn[1]);
  }

  // ---------- Per-page row walker ----------
  let currentEntry: TransactionEntry | null = null;
  let lastDate: string | undefined;
  // Column anchors detected per page from the `Payments into`/`Payments out`
  // header row. Updated every time we start a new transactions page.
  let columnAnchors: ColumnAnchors | null = null;
  // Buffer for a leg row whose fund name wrapped to the next pdfjs row.
  let pendingLegText: string | null = null;

  for (const page of pdf.pages) {
    const rows = groupItemsByRow(page.items, 2.2);
    // Refresh column anchors from this page's header row (if present)
    const pageAnchors = detectColumnAnchors(rows);
    if (pageAnchors) columnAnchors = pageAnchors;
    for (const row of rows) {
      const rowText = row.map((i) => i.str).join(' ').trim();
      if (!rowText) continue;

      // Skip page header / footer noise
      if (/policy number|standardlife\.ie|page \d+ of/i.test(rowText)) continue;
      if (/^transactions$/i.test(rowText)) continue;

      // Detect a date anywhere in the left cluster (pdfjs sometimes emits a
      // whitespace spacer at row[0] before the real date item).
      const rowDate = findDateInRow(row);
      if (rowDate) lastDate = rowDate;

      // Identify header label (strips leading date before matching the label)
      const type = detectTransactionType(rowText);
      if (type && lastDate) {
        if (currentEntry) out.entries.push(currentEntry);
        const { amountIn, amountOut } = extractAmountsFromRow(row, columnAnchors);
        // Strip the leading date (if any) and the amount tokens from the description
        const cleanedDescription = rowText
          .replace(/^\d{1,2}\s+[A-Za-z]{3,9}\s+\d{4}\s+/, '')
          .replace(/[€¤]\s*[\d.,]+/g, '')
          .replace(/\s+/g, ' ')
          .trim();
        currentEntry = {
          processedDate: lastDate,
          type,
          description: cleanedDescription || labelForType(type),
          amountIn: amountIn ?? null,
          amountOut: amountOut ?? null,
          allocationRatePercent: null,
          contributionPaid: null,
          legs: [],
          rawText: rowText,
        };
        // For SWITCH entries, the header row itself is the `Switch from …`
        // leg — parse and capture it so legs aren't missing the source.
        if (type === 'SWITCH') {
          const fromLeg = parseLeg(rowText);
          if (fromLeg) currentEntry.legs.push(fromLeg);
        }
        pendingLegText = null;
        continue;
      }

      if (!currentEntry) continue;

      // Sub-row: allocation rate
      const allocation = rowText.match(/Allocation rate\s*([\d.]+)\s*%/i);
      if (allocation) {
        currentEntry.allocationRatePercent = parseFloat(allocation[1]);
        continue;
      }

      // Sub-row: contribution paid
      const contribution = rowText.match(/Contribution paid\s*[€¤]?\s*([\d.,]+)/i);
      if (contribution) {
        currentEntry.contributionPaid = parseAmount(contribution[1]);
        continue;
      }

      // Sub-row: leg (Buy / Switch from / Switch to / Sell). Legs may wrap
      // across two pdfjs rows when the fund name is long — buffer and retry.
      const combined: string = pendingLegText ? `${pendingLegText} ${rowText}` : rowText;
      const leg = parseLeg(combined);
      if (leg) {
        currentEntry.legs.push(leg);
        pendingLegText = null;
        continue;
      }
      if (
        /^(Buy|Sell|Switch from|Switch to)\b/i.test(combined) &&
        !/=\s*[€¤]?\s*[\d.,]+\s*$/.test(combined)
      ) {
        pendingLegText = combined;
        continue;
      }
      pendingLegText = null;

      // Anything else: append to rawText for audit
      currentEntry.rawText += `\n${rowText}`;
    }
  }
  if (currentEntry) out.entries.push(currentEntry);

  // Defensive sanity check: sum(amountIn) should match header totalPaymentsIn.
  const sumAmountIn = out.entries.reduce((s, e) => s + (e.amountIn ?? 0), 0);
  if (
    out.totalPaymentsIn !== undefined &&
    Math.abs(sumAmountIn - out.totalPaymentsIn) > 0.01
  ) {
    // eslint-disable-next-line no-console
    console.warn(
      `[provider-form] transaction extraction total mismatch: sum(amountIn)=${sumAmountIn.toFixed(2)} vs header totalPaymentsIn=${out.totalPaymentsIn.toFixed(2)}`,
    );
  }

  // Derive totalPaymentsOut from entries if the header didn't surface it
  if (out.totalPaymentsOut === undefined) {
    out.totalPaymentsOut = out.entries.reduce((sum, e) => sum + (e.amountOut ?? 0), 0);
  }

  return out;
}

// ============================================================================
// Helpers
// ============================================================================

function findDateInRow(row: PdfTextItem[]): string | undefined {
  for (const item of row) {
    if (item.x > 120) break; // past the Date column
    const m = item.str.trim().match(/^(\d{1,2})\s+([A-Za-z]{3,9})\s+(\d{4})$/);
    if (m) return parseLongDate(`${m[1]} ${m[2]} ${m[3]}`);
  }
  return undefined;
}

function detectTransactionType(rowText: string): TransactionEntryType | null {
  // Strip an optional leading date (e.g. "24 Jun 2024 ") before matching
  // the label. Standard Life prints the first row of each transaction as
  // "<date>   <label>   <amount>" and subsequent repeats as "<label> <amount>"
  // with no date, so both forms need to match the same labels.
  const tail = rowText
    .replace(/^\d{1,2}\s+[A-Za-z]{3,9}\s+\d{4}\s+/, '')
    .trimStart();

  if (/^Regular contribution\b/i.test(tail)) return 'REGULAR_CONTRIBUTION';
  if (/^Single contribution\b/i.test(tail)) return 'SINGLE_CONTRIBUTION';
  // Switches don't have a "header" row with a total — the first leg
  // itself (`Switch from …`) is the header. We treat that as the SWITCH
  // entry and attach subsequent `Switch to …` rows as legs.
  if (/^Switch from\b/i.test(tail)) return 'SWITCH';
  if (/^Sell\b/i.test(tail)) return 'SELL';
  if (/^(Charge|Fee)\b/i.test(tail)) return 'CHARGE';
  return null;
}

function labelForType(type: TransactionEntryType): string {
  switch (type) {
    case 'REGULAR_CONTRIBUTION': return 'Regular contribution';
    case 'SINGLE_CONTRIBUTION': return 'Single contribution';
    case 'SWITCH': return 'Switch';
    case 'BUY': return 'Buy';
    case 'SELL': return 'Sell';
    case 'CHARGE': return 'Charge';
    default: return 'Other';
  }
}

interface ColumnAnchors {
  paymentsInX: number;
  paymentsOutX: number;
}

/**
 * Detects the `Payments into your policy` / `Payments out of your policy`
 * column header row on a page and returns its X positions. The header is
 * rendered as two rows (e.g. `Payments into | Payments out of` on one Y
 * line and `your policy | your policy` just below) — we use the top
 * labels because they're the most distinctive.
 */
function detectColumnAnchors(rows: PdfTextItem[][]): ColumnAnchors | null {
  for (const row of rows) {
    const intoItem = row.find((i) => /^Payments into$/i.test(i.str.trim()));
    const outItem = row.find((i) => /^Payments out(?: of)?$/i.test(i.str.trim()));
    if (intoItem && outItem) {
      return {
        paymentsInX: intoItem.x,
        paymentsOutX: outItem.x,
      };
    }
  }
  return null;
}

/**
 * Classify each currency cell on a transaction header row as `amountIn`
 * or `amountOut` using the column anchors detected from the page header.
 * Falls back to a midpoint heuristic if no anchors are available yet.
 */
function extractAmountsFromRow(
  row: PdfTextItem[],
  anchors: ColumnAnchors | null,
): { amountIn?: number; amountOut?: number } {
  const result: { amountIn?: number; amountOut?: number } = {};

  // Currency cells either appear as "¤150.00" in one item, or as "¤" + "150.00"
  // in two adjacent items. Handle both.
  const amounts: Array<{ x: number; value: number }> = [];
  for (let i = 0; i < row.length; i++) {
    const item = row[i];
    const s = item.str.trim();
    const singleCell = s.match(/^[€¤]\s*([\d.,]+)$/);
    if (singleCell) {
      const value = parseAmount(singleCell[1]);
      if (!Number.isNaN(value)) amounts.push({ x: item.x, value });
      continue;
    }
    // "¤" token followed by bare number
    if (s === '¤' || s === '€') {
      const next = row[i + 1];
      if (next && /^[\d.,]+$/.test(next.str.trim())) {
        const value = parseAmount(next.str);
        if (!Number.isNaN(value)) amounts.push({ x: item.x, value });
        i++;
      }
    }
  }

  if (amounts.length === 0) return result;

  // With anchors: assign each amount to the nearest column.
  if (anchors) {
    const midpoint = (anchors.paymentsInX + anchors.paymentsOutX) / 2;
    for (const a of amounts) {
      if (a.x >= midpoint && result.amountOut === undefined) {
        result.amountOut = a.value;
      } else if (a.x < midpoint && result.amountIn === undefined) {
        result.amountIn = a.value;
      }
    }
    return result;
  }

  // No anchors yet — fall back to a generous heuristic. Transactions
  // amounts sit right of x=400 in every Standard Life layout we've
  // seen; classify by the observed midpoint of this row's amounts.
  if (amounts.length === 1) {
    result.amountIn = amounts[0].value;
  } else {
    amounts.sort((a, b) => a.x - b.x);
    result.amountIn = amounts[0].value;
    result.amountOut = amounts[amounts.length - 1].value;
  }
  return result;
}

function parseLeg(rowText: string): TransactionLeg | null {
  const re =
    /(Buy|Sell|Switch from|Switch to)\s+([\d.,]+)\s+([A-Za-z][A-Za-z0-9 &/'\-()]+?)\s*@\s*([\d.,]+)\s*c\s*=\s*[€¤]?\s*([\d.,]+)/i;
  const m = rowText.match(re);
  if (!m) return null;
  const action: TransactionLegAction =
    m[1].toLowerCase() === 'buy' ? 'BUY'
    : m[1].toLowerCase() === 'sell' ? 'SELL'
    : m[1].toLowerCase() === 'switch from' ? 'SWITCH_FROM'
    : 'SWITCH_TO';
  return {
    action,
    units: parseAmount(m[2]),
    fundName: m[3].trim(),
    unitPriceCents: parseAmount(m[4]),
    amount: parseAmount(m[5]),
  };
}

function parseAmount(raw: string): number {
  return parseFloat(raw.replace(/,/g, '').replace(/[€¤]/g, '').trim());
}

const MONTHS: Record<string, number> = {
  january: 0, february: 1, march: 2, april: 3, may: 4, june: 5,
  july: 6, august: 7, september: 8, october: 9, november: 10, december: 11,
  jan: 0, feb: 1, mar: 2, apr: 3, jun: 5, jul: 6, aug: 7, sep: 8, sept: 8, oct: 9, nov: 10, dec: 11,
};

function parseLongDate(input: string | undefined): string | undefined {
  if (!input) return undefined;
  const m = input.match(/(\d{1,2})\s+([A-Za-z]+)\s+(\d{4})/);
  if (!m) return undefined;
  const day = parseInt(m[1], 10);
  const month = MONTHS[m[2].toLowerCase()];
  const year = parseInt(m[3], 10);
  if (month === undefined) return undefined;
  const d = new Date(Date.UTC(year, month, day));
  if (Number.isNaN(d.getTime())) return undefined;
  return d.toISOString().slice(0, 10);
}
